import { useEffect, useRef, useState } from 'react';
import useWorldStore from '../store/worldStore';

const mono = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" };

function getTaskButtonTargetPos() {
  if (typeof window === 'undefined') {
    return { x: 0, y: 0 };
  }

  const btn = document.querySelector('[data-tasks-btn]');
  if (!btn) {
    return { x: window.innerWidth - 200, y: 20 };
  }

  const rect = btn.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

function Toast({ toast, index, onDone }) {
  const [phase, setPhase] = useState('enter'); // enter | hold | fly | done
  const onDoneRef = useRef(onDone);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    // Enter → hold after 0.3s
    const t1 = setTimeout(() => setPhase('hold'), 300);
    // Hold → fly after 3s
    const t2 = setTimeout(() => setPhase('fly'), 3000);
    // Fly → done after 3.8s
    const t3 = setTimeout(() => {
      setPhase('done');
      onDoneRef.current();
    }, 3800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  // Get task button position for fly target
  const [targetPos] = useState(() => getTaskButtonTargetPos());

  if (phase === 'done') return null;

  const startX = window.innerWidth / 2;
  const startY = window.innerHeight - 90 - index * 56;

  let style = {};
  if (phase === 'enter') {
    style = {
      left: startX, top: startY + 20,
      opacity: 0, transform: 'translateX(-50%) scale(1)',
      transition: 'all 0.3s ease-out',
    };
    requestAnimationFrame(() => {
      const el = document.getElementById(`toast-${toast.id}`);
      if (el) { el.style.opacity = '1'; el.style.top = `${startY}px`; }
    });
  } else if (phase === 'hold') {
    style = {
      left: startX, top: startY,
      opacity: 1, transform: 'translateX(-50%) scale(1)',
      transition: 'all 0.15s ease-out',
    };
  } else if (phase === 'fly') {
    style = {
      left: targetPos.x, top: targetPos.y,
      opacity: 0, transform: 'translate(-50%, -50%) scale(0.3)',
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    };
  }

  const color = toast.agentColor || 'rgba(255,255,255,0.5)';

  return (
    <div id={`toast-${toast.id}`} style={{
      position: 'fixed',
      zIndex: 150,
      pointerEvents: 'none',
      ...style,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'rgba(6,6,7,0.95)',
        border: `1px solid ${color}66`,
        borderRadius: 14, padding: '10px 18px',
        boxShadow: `0 6px 24px rgba(0,0,0,0.5), 0 0 20px ${color}22`,
        whiteSpace: 'nowrap',
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: color,
          animation: 'toastDotPulse 1s ease-in-out infinite',
          flexShrink: 0,
        }} />
        <span style={{ fontSize: 16 }}>⚡</span>
        <span style={{ ...mono, fontSize: 13, color: 'white', fontWeight: 500 }}>
          {toast.text}
        </span>
      </div>
      <style>{`@keyframes toastDotPulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.5; transform:scale(1.4); } }`}</style>
    </div>
  );
}

export default function TaskToasts() {
  const toasts = useWorldStore((s) => s.taskToasts);
  const removeTaskToast = useWorldStore((s) => s.removeTaskToast);

  if (toasts.length === 0) return null;

  return (
    <>
      {toasts.map((toast, i) => (
        <Toast key={toast.id} toast={toast} index={i}
          onDone={() => removeTaskToast(toast.id)} />
      ))}
    </>
  );
}
